import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import type {
  FirstSendPreview,
  FirstSendRequestSummary,
  FirstSendRequestView,
  FirstSendScopeBody,
} from '../../api/types';
import { useV07Commands } from '../data/commands-context';
import { rotationWarning, sendAct, sendFailureText, sendReasonText } from '../lib/bulk-send';
import { deliveryText, firstSendHeadline, summariseDeliveries } from '../lib/first-send';
import { newIdempotencyKey } from '../lib/linkedin-member-post';
import { fmtDateTime } from '../lib/reference';
import { Modal, ModalHead } from '../ui/Modal';
import { useToast } from '../ui/toast';

/**
 * A SEND THE OPERATOR ASKS FOR: the late first e-mail, a deliberate re-send, or a reminder.
 *
 * One component for every place a send is offered — one record from its detail screen, the records
 * selected in the register, a whole upload from its own page, a group — because they are the same
 * act and several implementations would be several behaviours. What differs between them is the
 * SCOPE handed in and the KIND asked for.
 *
 * The three kinds are genuinely different acts and the window says which one it is in its heading,
 * its rule line, its button and its result: `kind` selects the vocabulary in `lib/bulk-send.ts`,
 * and nothing in here hard-codes a sentence about one of them. What they share is everything that
 * made the first send trustworthy — one durable request row, one idempotency key per press, the
 * outbox doing the work, and a result that can be read again after the window is shut.
 *
 * WHAT IT SHOWS, AND WHY IN THAT ORDER
 * ------------------------------------
 * The numbers first, before anything is sent: how many records the scope names, how many will be
 * sent, how many will not and why each. They are the server's numbers — a browser counting the rows
 * it happens to be holding is how a total nobody can reconcile gets onto a screen.
 *
 * Then, once accepted, the progress of the accepted request, read back by its id. That is not a
 * detail of the implementation: the work happens in the outbox worker, so the window can be closed
 * and the result read afterwards — and `openRequestId` is how a later visit comes straight back to
 * it, rather than starting a second send to find out what the first one did.
 *
 * The automatic follow ends when every target is settled, because queuing is what it follows. The
 * delivery beside it does not end there: the provider is handed the message later, and the window
 * may still be open. «Durumu yenile» is that one read, asked for by hand — the same read by the
 * same id, never a second send.
 *
 * WHAT IT NEVER SAYS
 * ------------------
 * It never calls this a re-send or a reminder: it is the first e-mail, late. And it never says a
 * message was sent or delivered on the strength of a target's status — a target stops at «queued»,
 * which is this request handing the message to the outbox. Provider acceptance and delivery are
 * facts of the delivery row, and only what that row says is shown.
 */

export interface FirstSendPanelProps {
  /**
   * Which act. Omitted means the first send — what every caller written before the other two
   * existed asked for, and what the server still defaults to.
   */
  kind?: string;
  scope: FirstSendScopeBody;
  /** What the operator is about to act on, in their words: «3 seçili kayıt», «bu kayıt», … */
  what: string;
  /** Open straight onto an accepted request instead of a new one. */
  openRequestId?: string;
  /** Narrows «earlier sends» to the requests that covered this record. */
  credentialId?: string;
  /**
   * The delivery may have moved since the host screen last read it.
   *
   * Called after a successful hand-pressed read and when the window closes, so the badge behind it
   * is not left saying «Kuyrukta» about a message this window has just shown as delivered. It is
   * the HOST's own read that re-runs — `useRead`'s `retry`, which keeps the last answer when it
   * fails — so nothing here is a second source of truth, and it is never called from the automatic
   * follow: a read that re-triggered a read is the loop `invalidate-reads.ts` exists to prevent.
   */
  onDeliveryMoved?: () => void;
  onClose: () => void;
}

const SETTLED = new Set(['queued', 'skipped', 'failed']);

/** How long to wait before the next progress read, after this many consecutive failures. */
const BACKOFF_MS = [1500, 3000, 6000, 10_000, 15_000] as const;

export function FirstSendPanel({
  kind = 'first_send',
  scope,
  what,
  openRequestId,
  credentialId,
  onDeliveryMoved,
  onClose,
}: FirstSendPanelProps): ReactElement {
  const commands = useV07Commands();
  const toast = useToast();
  const act = sendAct(kind);
  const [preview, setPreview] = useState<FirstSendPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState(act.defaultReason);
  const [sending, setSending] = useState(false);
  const [view, setView] = useState<FirstSendRequestView | null>(null);
  const [earlier, setEarlier] = useState<FirstSendRequestSummary[]>([]);
  /** How many progress reads in a row have failed, and whether one is outstanding. */
  const [offline, setOffline] = useState(0);
  /** A hand-pressed read is in flight; the button says so and cannot be pressed again. */
  const [refreshing, setRefreshing] = useState(false);
  /*
   * The name of THIS press, minted when the window opens.
   *
   * Only the caller can tell a retry of the press it already made from a second intent, so the key
   * travels with the request and its retries. Opening the window again is a new intent and gets a
   * new key — which is the deliberate second act an operator is entitled to make, and which the
   * server will still refuse to turn into a second e-mail for a record that already has one.
   */
  const key = useRef(newIdempotencyKey());
  /*
   * The accepted request's id, kept OUTSIDE the view.
   *
   * The acceptance and the first read of its result are two calls, and the second one can fail. If
   * the id lived only inside the view, that failure would lose the request — and the only way back
   * to it would be a second send. It is written the moment the server answers, and everything
   * afterwards is a read that may be retried.
   */
  const requestId = useRef<string | null>(openRequestId ?? null);
  /*
   * So a late answer cannot paint a panel the operator has already closed.
   *
   * It is set true ON MOUNT and not merely at construction, because StrictMode mounts, unmounts
   * and mounts again: without the first line the teardown of that rehearsal leaves the flag false
   * for the life of the real panel, and every answer — the preview included — is dropped on
   * arrival. The browser caught exactly that; a window stuck on «Kapsam okunuyor…».
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Read the accepted request once. Answers whether the read itself landed.
   *
   * A failure here is a failure of the READ, never of the send: the request exists, the worker is
   * working it, and the only thing that went wrong is this question.
   */
  const readOnce = useCallback(
    async (id: string): Promise<boolean> => {
      const r = await commands.readFirstSendRequest({ requestId: id });
      if (!alive.current) return true;
      if (r.ok && r.view) {
        setView(r.view);
        setOffline(0);
        return true;
      }
      setOffline((n) => n + 1);
      return false;
    },
    [commands],
  );

  const scopeToken = JSON.stringify(scope);
  // biome-ignore lint/correctness/useExhaustiveDependencies: the scope is the question and `scopeToken` is its identity; the object itself would re-ask on every render and `commands` would re-ask whenever any command is rebound
  useEffect(() => {
    if (openRequestId !== undefined) return;
    let current = true;
    setPreview(null);
    setError(null);
    void (async () => {
      const r = await commands.previewFirstSend({ kind, scope });
      if (!current || !alive.current) return;
      if (!r.ok || !r.preview) {
        setError(r.message ?? 'Kapsam okunamadı');
        return;
      }
      setPreview(r.preview);
    })();
    return () => {
      current = false;
    };
  }, [scopeToken, openRequestId, kind]);

  /* The requests already accepted, so one of them can be opened instead of starting another. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: read once when the window opens; `commands` is rebound on every store write and would re-ask each time
  useEffect(() => {
    void (async () => {
      const r = await commands.listFirstSendRequests({
        ...(credentialId === undefined ? {} : { credentialId }),
        limit: 5,
      });
      if (alive.current && r.ok && r.requests) setEarlier(r.requests);
    })();
  }, [credentialId]);

  /* Opened onto an existing request: read it straight away. */
  // biome-ignore lint/correctness/useExhaustiveDependencies: `readOnce` is stable for a given command object and re-running on it would re-read for no reason
  useEffect(() => {
    if (openRequestId === undefined) return;
    void readOnce(openRequestId);
  }, [openRequestId]);

  /*
   * While an accepted request is outstanding, follow it — and keep following it after a failure.
   *
   * A single failed read used to end the follow for good, which left an operator watching a
   * half-finished list with no way to learn it had finished. The interval backs off so a server
   * that is down is not hammered, and the panel says plainly that it is the CONNECTION that is
   * failing rather than the send.
   */
  useEffect(() => {
    const id = requestId.current;
    if (id === null) return;
    const outstanding =
      view === null || Object.entries(view.counts).some(([k, n]) => !SETTLED.has(k) && n > 0);
    if (!outstanding) return;
    const wait = BACKOFF_MS[Math.min(offline, BACKOFF_MS.length - 1)] as number;
    const timer = window.setTimeout(() => {
      void readOnce(id);
    }, wait);
    return () => window.clearTimeout(timer);
  }, [view, offline, readOnce]);

  const send = async (): Promise<void> => {
    if (sending || reason.trim().length < 3) return;
    setSending(true);
    try {
      const r = await commands.requestFirstSend({
        kind,
        scope,
        reason: reason.trim(),
        idempotencyKey: key.current,
      });
      if (!alive.current) return;
      if (!r.ok || !r.requestId) {
        /*
         * A lost answer is NOT a refusal.
         *
         * `outcome: 'unresolved'` means the browser never reached the server or the server fell
         * over — the request may well have been accepted, and the key proves which one it would
         * be. Reporting that as «gönderim reddedildi» would be a false statement AND would push
         * the operator towards a second press with a second key. Asking again with the SAME key is
         * what reconciles it: the server answers the request that already exists, or makes it.
         */
        if (r.outcome === 'unresolved') {
          toast('Sunucuya ulaşılamadı; aynı işlem anahtarıyla yeniden deneniyor…', 'warn');
          const again = await commands.requestFirstSend({
            kind,
            scope,
            reason: reason.trim(),
            idempotencyKey: key.current,
          });
          if (!alive.current) return;
          if (again.ok && again.requestId) {
            requestId.current = again.requestId;
            toast(
              again.created
                ? 'Gönderim kuyruğa alındı; pencereyi kapatsanız da sürüyor'
                : 'Bu gönderim zaten kabul edilmişti; ikinci bir gönderim oluşturulmadı',
              'ok',
            );
            await readOnce(again.requestId);
            return;
          }
          setError(
            'Sunucuya ulaşılamadı. Bu gönderim kabul edilmiş olabilir; pencereyi kapatıp ' +
              '«Daha önce kabul edilmiş gönderimler» listesinden kontrol edin. Aynı işlem ' +
              'anahtarıyla ikinci bir e-posta oluşmaz.',
          );
          return;
        }
        toast(r.message ?? 'Gönderim kabul edilmedi', 'danger');
        return;
      }
      requestId.current = r.requestId;
      /*
       * «Kuyruğa alındı», never «gönderildi». The request's job is to queue the messages; whether
       * the provider then accepted one is the delivery's own outcome and is shown as such.
       */
      toast(
        r.created
          ? 'Gönderim kuyruğa alındı; pencereyi kapatsanız da sürüyor'
          : 'Bu gönderim daha önce kabul edilmişti; sonucu aşağıda',
        'ok',
      );
      /* The id is already kept; a failed first read is retried rather than losing the request. */
      await readOnce(r.requestId);
    } finally {
      if (alive.current) setSending(false);
    }
  };

  /**
   * Read the accepted request again, because somebody asked.
   *
   * The automatic follow stops once every target is settled — queuing is what it was following —
   * but a DELIVERY goes on living after that: the outbox hands it to the provider later, and the
   * window may still be open when it does. So the result gets one read it can be asked for.
   *
   * It is the same read as every other: `readFirstSendRequest` by the id this panel already holds.
   * No new request, no new idempotency key, no second e-mail — and nothing is cleared first, so a
   * read that fails leaves the result exactly as it was and can simply be asked for again.
   */
  const refresh = async (): Promise<void> => {
    const id = requestId.current;
    if (id === null || refreshing) return;
    setRefreshing(true);
    try {
      /* Only a read that LANDED says anything about the world; a failed one leaves the host's
         badge alone, exactly as it leaves this window's own result alone. */
      if (await readOnce(id)) onDeliveryMoved?.();
    } finally {
      if (alive.current) setRefreshing(false);
    }
  };

  /* Either rule's vocabulary: the codes that belong to one act alone are disjoint by construction. */
  const reasonText = (code: string): string => sendReasonText(code);

  const following = requestId.current !== null;
  /*
   * Before the send, this window is the question; afterwards it is the answer, and the heading says
   * which. A reader who comes back to «İlk e-postayı gönder» reasonably believes nothing has been
   * sent yet.
   */
  /*
   * The heading follows the REQUEST once there is one, not the kind this window was opened with:
   * «Daha önce kabul edilmiş gönderimler» can open a reminder from inside a re-send window, and a
   * result headed «Yeniden gönderim sonucu» would then be describing something else.
   */
  const title = following ? (view === null ? act : sendAct(view.request.kind)).resultTitle : act.askTitle;
  /* Leaving the result is also a moment the host should re-read: this window may have learned
     something the screen behind it has not. A window that never followed a request learned
     nothing, so it asks for nothing. */
  const close = (): void => {
    if (requestId.current !== null) onDeliveryMoved?.();
    onClose();
  };
  return (
    <Modal label={title} onClose={close} wide>
      <ModalHead title={title} onClose={close} />
      <div className="card-pad" id="fsPanel">
        {offline > 0 && following ? (
          <p className="warn" id="fsOffline" role="status">
            {`Sonuç okunamıyor (${offline}. deneme). Gönderim sunucuda sürüyor; bağlantı gelince ` +
              'bu liste kendiliğinden tazelenir.'}
          </p>
        ) : null}
        {following ? (
          view === null ? (
            <p className="muted" id="fsLoadingResult">
              Gönderim kabul edildi; sonucu okunuyor…
            </p>
          ) : (
            <FirstSendResult view={view} reasonText={reasonText} />
          )
        ) : (
          <>
            <p className="muted" id="fsRule">
              {/* The act, then the scope in the operator's words, then what will and will not happen. */}
              {`İşlem: ${act.name}. Kapsam: ${what}. ${act.rule} `}
              Kayıtlar yeniden üretilmez; mevcut sertifikalar kullanılır.
            </p>
            {error !== null ? (
              <p className="danger" id="fsError">
                {error}
              </p>
            ) : preview === null ? (
              <p className="muted" id="fsLoading">
                Kapsam okunuyor…
              </p>
            ) : (
              <>
                <table className="table" id="fsCounts">
                  <tbody>
                    <tr>
                      <th>İşlem türü</th>
                      {/* The server's own answer about which rule produced these numbers. */}
                      <td id="fsKind" data-kind={preview.kind}>
                        {sendAct(preview.kind).name}
                      </td>
                    </tr>
                    <tr>
                      <th>Seçilen kayıt</th>
                      <td id="fsSelected">{preview.selected}</td>
                    </tr>
                    <tr>
                      <th>Gönderilecek</th>
                      <td id="fsEligible">{preview.eligible}</td>
                    </tr>
                    <tr>
                      {/*
                       * One address may hold several certificates, and each one is its own message:
                       * nothing is merged and no certificate is skipped for sharing an address. The
                       * two numbers are kept apart so that is visible before the send.
                       */}
                      <th>Benzersiz alıcı</th>
                      <td id="fsRecipients">{preview.uniqueRecipients}</td>
                    </tr>
                  </tbody>
                </table>
                {preview.eligible > preview.uniqueRecipients ? (
                  <p className="muted small" id="fsPerCertificate">
                    {`${preview.eligible} sertifika için ${preview.uniqueRecipients} ayrı adrese gönderilir; ` +
                      'aynı adresteki her sertifika kendi e-postasını alır, mesajlar birleştirilmez.'}
                  </p>
                ) : null}
                {/*
                 * WHAT A CLAIM-CARRYING SEND COSTS, before it is accepted.
                 *
                 * The raw token is never stored, only its hash, so the link already in somebody's
                 * hands cannot be rebuilt: a new acceptance e-mail mints a new one and the old one
                 * stops working. Saying that afterwards is not the same as saying it first.
                 */}
                {rotationWarning(preview.rotatingTokens) !== null ? (
                  <p className="warn" id="fsRotation">
                    {rotationWarning(preview.rotatingTokens)}
                  </p>
                ) : null}
                {/*
                 * Every record that will NOT be sent, with its reason. Without this the operator is
                 * left with the difference between two numbers and no way to account for it.
                 */}
                {preview.skipped.length > 0 ? (
                  <>
                    <h3>İşleme alınmayacak kayıtlar</h3>
                    <ul id="fsSkipped">
                      {preview.skipped.map((s) => (
                        <li key={s.reason}>
                          <strong>{s.count}</strong>
                          {` kayıt — ${reasonText(s.reason)}`}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {preview.truncated ? (
                  <p className="danger" id="fsTruncated">
                    {`Filtre ${preview.matching} kayıt getiriyor; tek işlemde en çok ${preview.limit} kayıt işlenebilir. Filtreyi daraltın.`}
                  </p>
                ) : null}
                {preview.eligible === 0 ? (
                  <p className="muted" id="fsNone">
                    {act.none}
                  </p>
                ) : null}
                <label className="field" htmlFor="fsReason">
                  <span>Gerekçe</span>
                  <input
                    id="fsReason"
                    className="input"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    maxLength={500}
                  />
                </label>
              </>
            )}
            {/*
             * The sends already accepted, so the way to see an old result is to open it — not to
             * start a new one. They outlive this window, this tab and this session.
             */}
            {earlier.length > 0 ? (
              <>
                <h3>Daha önce kabul edilmiş gönderimler</h3>
                {/*
                 * Every kind, not just this one, and each named: the history is «what has been sent
                 * to these records», and hiding the other two acts from it would be the fastest way
                 * to a second send that nobody meant.
                 *
                 * What is shown per row is the act, the scope, who started it, when, and how its
                 * records ended up — all five from the server's own row.
                 */}
                <ul id="fsEarlier">
                  {earlier.map((r) => (
                    <li key={r.id} data-kind={r.kind}>
                      <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        data-open-request={r.id}
                        onClick={() => {
                          requestId.current = r.id;
                          setOffline(0);
                          void readOnce(r.id);
                        }}
                      >
                        Sonucu aç
                      </button>
                      {` ${sendAct(r.kind).name} · ${scopeLine(r.scope)} · ${
                        r.requestedByLabel ?? 'başlatan bilinmiyor'
                      } · ${fmtDateTime(r.createdAt)} · ${countsLine(r.counts)}`}
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
          </>
        )}
      </div>
      <div className="card-foot">
        <button className="btn btn-ghost" type="button" onClick={close} id="fsClose">
          {following ? 'Kapat' : 'Vazgeç'}
        </button>
        {following ? (
          /* The reference's own secondary button; it reads, and reading is all it does. */
          <button
            className="btn btn-secondary"
            type="button"
            id="fsRefresh"
            disabled={refreshing}
            onClick={() => void refresh()}
          >
            {refreshing ? 'Yenileniyor…' : 'Durumu yenile'}
          </button>
        ) : null}
        {following ? null : (
          <button
            className="btn btn-primary"
            type="button"
            id="fsSend"
            disabled={sending || preview === null || preview.eligible === 0 || reason.trim().length < 3}
            onClick={() => void send()}
          >
            {sending ? 'Gönderiliyor…' : `${act.action} (${preview?.eligible ?? 0})`}
          </button>
        )}
      </div>
    </Modal>
  );
}

/**
 * What a request covered, from the scope it was accepted with.
 *
 * It is the scope AS ASKED, so a filter is described as a filter rather than re-resolved: what it
 * matches today is not what the request covers, and saying «12 kayıt» from a fresh count would be
 * the one thing the freezing exists to prevent.
 */
function scopeLine(scope: Record<string, unknown>): string {
  /* The server's own shape, read as what it is rather than indexed key by key. */
  const asked = scope as {
    kind?: string;
    credentialIds?: unknown;
    filter?: {
      groupId?: string;
      batchId?: string;
      templateId?: string;
      status?: string;
      delivery?: string;
      noEmail?: boolean;
      q?: string;
    };
  };
  if (asked.kind === 'credentials') {
    const n = Array.isArray(asked.credentialIds) ? asked.credentialIds.length : 0;
    return `${n} seçili kayıt`;
  }
  const f = asked.filter ?? {};
  const named: string[] = [];
  if (typeof f.groupId === 'string') named.push('grup');
  if (typeof f.batchId === 'string') named.push('yükleme');
  if (typeof f.templateId === 'string') named.push('şablon');
  if (typeof f.status === 'string' && f.status !== '') named.push('durum');
  if (typeof f.delivery === 'string' && f.delivery !== '') named.push('teslim durumu');
  if (f.noEmail === true) named.push('e-posta gönderilmemiş');
  if (typeof f.q === 'string' && f.q !== '') named.push('arama');
  return named.length === 0 ? 'filtre (tüm kayıtlar)' : `filtre: ${named.join(' + ')}`;
}

/** «3 kuyruğa alındı · 1 işleme alınmadı», from the server's own counts and nothing else. */
function countsLine(counts: Record<string, number>): string {
  const at = (k: string): number => Object.entries(counts).find(([x]) => x === k)?.[1] ?? 0;
  const parts: string[] = [];
  if (at('pending') > 0) parts.push(`${at('pending')} sırada`);
  if (at('queued') > 0) parts.push(`${at('queued')} kuyruğa alındı`);
  if (at('skipped') > 0) parts.push(`${at('skipped')} işleme alınmadı`);
  if (at('failed') > 0) parts.push(`${at('failed')} başarısız`);
  return parts.length === 0 ? 'kayıt yok' : parts.join(' · ');
}

/**
 * What became of each record — the DELIVERY first, the request's own word second.
 *
 * The order is the whole point of this component. A reader who opens an accepted request wants to
 * know where the message is now; «kuyruğa alındı» is what the request did at the time, and when it
 * leads, a delivered e-mail reads as one still sitting in a queue. So the delivery summary is the
 * headline and the table's first status column, and the request's own counts are kept below under
 * their own name, as the operation detail they are.
 *
 * Nothing is rewritten to make this read better: the target's status is still the target's status
 * and the delivery's is still the delivery's. They are two facts, shown as two.
 */
function FirstSendResult({
  view,
  reasonText,
}: {
  view: FirstSendRequestView;
  reasonText: (code: string) => string;
}): ReactElement {
  /* The server groups the targets by status; a status it has none of is simply absent. */
  const at = (k: string): number => Object.entries(view.counts).find(([x]) => x === k)?.[1] ?? 0;
  const ops = { pending: at('pending'), queued: at('queued'), skipped: at('skipped'), failed: at('failed') };
  const delivery = summariseDeliveries(view.targets);
  return (
    <>
      {/* What this request was: the act, what it covered, and the words it was accepted with. */}
      <p className="muted small" id="fsWhat" data-kind={view.request.kind}>
        {`${sendAct(view.request.kind).name} · ${scopeLine(view.request.scope)} · ${fmtDateTime(
          view.request.createdAt,
        )} · «${view.request.reason}»`}
      </p>
      <p className="muted" id="fsProgress" data-worker={String(view.processing?.workerEnabled ?? '')}>
        {firstSendHeadline(delivery, ops, view.processing)}
      </p>
      {/*
       * The four states a queued message can be in, counted off the delivery rows and nothing
       * else. A target the request never queued is not in any of them — it has no delivery to be
       * in a state — and is counted in the operation summary below instead, which until this round
       * the summary did not do: the sentence was true of the intent and false of the table.
       */}
      {delivery.deliveries > 0 ? (
        <table className="table" id="fsDelivery">
          <tbody>
            <tr>
              <th>Gönderim bekleyen</th>
              <td id="fsDlvWaiting">{delivery.onTheWay}</td>
            </tr>
            <tr>
              <th>Sağlayıcının kabul ettiği, teslim teyidi bekleyen</th>
              <td id="fsDlvAccepted">{delivery.accepted}</td>
            </tr>
            <tr>
              <th>Teslim edilen</th>
              <td id="fsDlvDelivered">{delivery.delivered}</td>
            </tr>
            <tr>
              <th>Teslim hatası bulunan</th>
              <td id="fsDlvFailed">{delivery.failed}</td>
            </tr>
            {/* Only when there is one: a status this product has not learned yet. */}
            {delivery.unknown > 0 ? (
              <tr>
                <th>Durumu okunamayan</th>
                <td id="fsDlvUnknown">{delivery.unknown}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
      ) : null}
      <h3>İşlem özeti</h3>
      <p className="muted small" id="fsOpsNote">
        Bu sayılar gönderim talebinin kendi sonucudur: kaç kaydın mesajı kuyruğa alındı. Teslim bilgisi
        yukarıdaki durumlardan okunur.
      </p>
      <table className="table" id="fsOutcome">
        <tbody>
          {/*
           * THE ROW THAT WAS MISSING, AND WHAT ITS ABSENCE COST.
           *
           * The server has always counted `pending` — a target nobody has claimed yet — and this
           * table printed only the three states that are DONE. So an accepted request whose single
           * record was still waiting showed «Kuyruğa alınan 0 · İşleme alınmayan 0 · İşleme
           * sırasında başarısız 0», three zeros that read as «nothing happened at all» about a
           * request that had in fact been accepted and was waiting. It is printed only when there
           * is one, so a finished request keeps the shape it has always had.
           */}
          {ops.pending > 0 ? (
            <tr>
              <th>Sırada bekleyen</th>
              <td id="fsPending">{ops.pending}</td>
            </tr>
          ) : null}
          <tr>
            <th>Kuyruğa alınan</th>
            <td id="fsQueued">{ops.queued}</td>
          </tr>
          <tr>
            <th>İşleme alınmayan</th>
            <td id="fsSkippedCount">{ops.skipped}</td>
          </tr>
          <tr>
            <th>İşleme sırasında başarısız</th>
            <td id="fsFailed">{ops.failed}</td>
          </tr>
        </tbody>
      </table>
      <table className="table" id="fsResult">
        <thead>
          <tr>
            <th>Kod</th>
            <th>Alıcı</th>
            <th>Güncel teslim durumu</th>
            <th>İşlem (gönderim talebi)</th>
          </tr>
        </thead>
        <tbody>
          {view.targets.map((t) => (
            <tr key={t.credentialId}>
              <td>{t.publicCode ?? '—'}</td>
              <td>{t.holderName ?? '—'}</td>
              {/* Strictly the delivery row's own word. No delivery, nothing to say. */}
              <td data-delivery={t.deliveryStatus ?? ''}>{deliveryText(t.deliveryStatus)}</td>
              <td className="muted small">
                {t.status === 'queued'
                  ? 'Kuyruğa alındı'
                  : t.status === 'pending'
                    ? 'Sırada'
                    : t.status === 'skipped'
                      ? `İşleme alınmadı — ${reasonText(t.reasonCode ?? '')}`
                      : `Başarısız — ${sendFailureText(t.reasonCode)}`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
